// src/host/EditListing.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../lib/useAuth";
import {
  getListingById as fetchListing, // alias
  updateListing,
  suggestDayRate,
  getHostVerificationStatus,
} from "../firebase.js";
import {
  AMENITY_CATALOG,
  AMENITY_ORDER,
  normalizeAmenityKey,
} from "../lib/amenities";
import {
  // for host profile fetch fallback
  doc,
  getDoc,
  serverTimestamp,
} from "firebase/firestore";

/** ------------------------------ Brand -------------------------------- **/
const BRAND_NAME = "You’re Welcome";
const BRAND = "#7C9082";

/** ------------------------------ Config -------------------------------- **/
const MAX_FILES = 20;
const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_FILE_MB = 12;
const IMG_MAX_DIM = 1600;
const IMG_QUALITY = 0.82;

/** ------------------------------ Helpers ------------------------------- **/
function clsx(...xs) {
  return xs.filter(Boolean).join(" ");
}
function asIntOrNull(v) {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
function normalizeStringList(input) {
  if (!input) return [];
  if (Array.isArray(input)) {
    return Array.from(
      new Set(input.map((s) => String(s || "").trim()).filter(Boolean))
    );
  }
  if (typeof input === "string") {
    return Array.from(
      new Set(
        input
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      )
    );
  }
  if (typeof input === "object") {
    return Object.keys(input).filter((k) => !!input[k]);
  }
  return [];
}
function titleCaseFromKey(k) {
  return String(k || "")
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

// canvas compress
async function compressImage(
  file,
  { maxDim = IMG_MAX_DIM, quality = IMG_QUALITY } = {}
) {
  if (!file || !file.type?.startsWith("image/")) return file;
  const bmp = await createImageBitmap(file);
  const scale = Math.min(1, maxDim / Math.max(bmp.width, bmp.height));
  const w = Math.round(bmp.width * scale);
  const h = Math.round(bmp.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bmp, 0, 0, w, h);
  const mime = file.type === "image/png" ? "image/png" : "image/jpeg";
  const blob = await new Promise((res) =>
    canvas.toBlob(res, mime, mime === "image/jpeg" ? quality : undefined)
  );
  if (!blob) return file;
  const ext = mime === "image/png" ? "png" : "jpg";
  const name = (file.name || "photo").replace(/\.(jpeg|jpg|png|webp)$/i, "");
  return new File([blob], `${name}.${ext}`, {
    type: mime,
    lastModified: Date.now(),
  });
}

/** ----------------------------- Edit Screen ---------------------------- **/
export default function EditListing() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams(); // /host/listings/edit/:id

  // Form state
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [capacity, setCapacity] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [stateCode, setStateCode] = useState("");

  const [spaceType, setSpaceType] = useState("entire_place");
  const [spaceName, setSpaceName] = useState("");
  const [entranceType, setEntranceType] = useState("shared");
  const [areas, setAreas] = useState([]); // string[]
  const [amenities, setAmenities] = useState([]); // canonical amenity keys

  const [bookingMode, setBookingMode] = useState("day");
  const [dayPrice, setDayPrice] = useState("");
  const [dayUseStartHour, setDayUseStartHour] = useState("");
  const [dayUseEndHour, setDayUseEndHour] = useState("");
  const [nightlyPrice, setNightlyPrice] = useState("");
  const [minNights, setMinNights] = useState("");
  const [maxNights, setMaxNights] = useState("");
  const [checkInHour, setCheckInHour] = useState("");
  const [checkOutHour, setCheckOutHour] = useState("");
  const [rules, setRules] = useState("");

  // Photos
  const [existingPhotoUrls, setExistingPhotoUrls] = useState([]); // strings (ordered; first=cover)
  const [deletedPhotoUrls, setDeletedPhotoUrls] = useState([]); // strings
  const [newFiles, setNewFiles] = useState([]); // File[]
  const fileInputRef = useRef(null);

  // UI / meta
  const [status, setStatus] = useState(null);
  const [loadingListing, setLoadingListing] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [verification, setVerification] = useState(null);

  // Dirty guard
  const initialSnapshotRef = useRef("");
  // New: track original location for "change address" check
  const originalLocationRef = useRef({
    address: "",
    city: "",
    stateCode: "",
  });

  const isDirty = useMemo(() => {
    const cur = JSON.stringify({
      title,
      summary,
      capacity,
      address,
      city,
      stateCode,
      spaceType,
      spaceName,
      entranceType,
      areas,
      amenities,
      bookingMode,
      dayPrice,
      dayUseStartHour,
      dayUseEndHour,
      nightlyPrice,
      minNights,
      maxNights,
      checkInHour,
      checkOutHour,
      rules,
      existingPhotoUrls,
      deletedPhotoUrls,
      newFiles: newFiles.map((f) => f.name + ":" + f.size),
    });
    return cur !== initialSnapshotRef.current;
  }, [
    title,
    summary,
    capacity,
    address,
    city,
    stateCode,
    spaceType,
    spaceName,
    entranceType,
    areas,
    amenities,
    bookingMode,
    dayPrice,
    dayUseStartHour,
    dayUseEndHour,
    nightlyPrice,
    minNights,
    maxNights,
    checkInHour,
    checkOutHour,
    rules,
    existingPhotoUrls,
    deletedPhotoUrls,
    newFiles,
  ]);

  // Warn on unload
  useEffect(() => {
    function beforeUnload(e) {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = "";
    }
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [isDirty]);

  // Load verification
  useEffect(() => {
    let mounted = true;
    async function run() {
      if (!user?.uid) return;
      try {
        const v = await getHostVerificationStatus({ uid: user.uid });
        if (mounted) setVerification(v);
      } catch {
        // ignore
      }
    }
    run();
    return () => {
      mounted = false;
    };
  }, [user?.uid]);

  // Load listing
  useEffect(() => {
    let mounted = true;
    async function loadListing() {
      if (!user?.uid || !id) return;
      setLoadingListing(true);
      setError("");
      try {
        const listing = await fetchListing(id);
        if (!listing) throw new Error("Listing not found.");
        if (listing.ownerUid !== user.uid) {
          throw new Error("You don't have permission to edit this listing.");
        }

        setTitle(listing.title || "");
        setSummary(listing.summary || "");
        setCapacity(String(listing.capacity ?? ""));
        setAddress(listing.address || "");
        setCity(listing.city || "");
        setStateCode((listing.stateCode || "").toUpperCase());

        setSpaceType(listing.spaceType || "entire_place");
        setSpaceName(listing.spaceName || "");
        setEntranceType(listing.entranceType || "shared");
        setAreas(Array.isArray(listing.areas) ? listing.areas : []);

        // canonicalize amenities
        const rawAmenities = Array.isArray(listing.amenities)
          ? listing.amenities
          : [];
        const canonAmenities = Array.from(
          new Set(
            rawAmenities.map((a) => normalizeAmenityKey(a)).filter(Boolean)
          )
        );
        setAmenities(canonAmenities);

        const br = listing.bookingRules || {};

const effectiveMinNights =
  br.minNights ?? listing.minNights ?? "";
const effectiveMaxNights =
  br.maxNights ?? listing.maxNights ?? "";

setBookingMode(listing.bookingMode || "day");
setDayPrice(String(listing.dayPrice ?? ""));
setDayUseStartHour(listing.dayUseStartHour ?? "");
setDayUseEndHour(listing.dayUseEndHour ?? "");
setNightlyPrice(String(listing.nightlyPrice ?? ""));
setMinNights(String(effectiveMinNights ?? ""));
setMaxNights(String(effectiveMaxNights ?? ""));
setCheckInHour(listing.checkInHour ?? "");
setCheckOutHour(listing.checkOutHour ?? "");
setRules(listing.rules || "");


        setExistingPhotoUrls(
          Array.isArray(listing.photos) ? listing.photos : []
        );
        setStatus(listing.status || "draft");

        // Save original location snapshot for "address changed" logic
        originalLocationRef.current = {
          address: listing.address || "",
          city: listing.city || "",
          stateCode: (listing.stateCode || "").toUpperCase(),
        };

        const snap = JSON.stringify({
          title: listing.title || "",
          summary: listing.summary || "",
          capacity: String(listing.capacity ?? ""),
          address: listing.address || "",
          city: listing.city || "",
          stateCode: (listing.stateCode || "").toUpperCase(),
          spaceType: listing.spaceType || "entire_place",
          spaceName: listing.spaceName || "",
          entranceType: listing.entranceType || "shared",
          areas: Array.isArray(listing.areas) ? listing.areas : [],
          amenities: canonAmenities,
          bookingMode: listing.bookingMode || "day",
          dayPrice: String(listing.dayPrice ?? ""),
          dayUseStartHour: listing.dayUseStartHour ?? "",
          dayUseEndHour: listing.dayUseEndHour ?? "",
          nightlyPrice: String(listing.nightlyPrice ?? ""),
          minNights: String(effectiveMinNights ?? ""),
          maxNights: String(effectiveMaxNights ?? ""),
          checkInHour: listing.checkInHour ?? "",
          checkOutHour: listing.checkOutHour ?? "",
          rules: listing.rules || "",
          existingPhotoUrls: Array.isArray(listing.photos) ? listing.photos : [],
          deletedPhotoUrls: [],
          newFiles: [],
        });
        if (mounted) {
          initialSnapshotRef.current = snap;
        }
      } catch (err) {
        console.error(err);
        if (mounted)
          setError(err?.message || "Could not load listing.");
      } finally {
        if (mounted) setLoadingListing(false);
      }
    }
    loadListing();
    return () => {
      mounted = false;
    };
  }, [user?.uid, id]);

  /** --------------------------- Photo handlers --------------------------- **/
  const onPickFiles = async (filesLike) => {
    const files = Array.from(filesLike || []);
    if (!files.length) return;

    let next = [...newFiles];
    const remaining = Math.max(
      0,
      MAX_FILES - existingPhotoUrls.length - next.length
    );
    const slice = files.slice(0, remaining);

    for (const f of slice) {
      const typeOk = ACCEPTED_TYPES.includes(f.type);
      const sizeMb = (f.size || 0) / (1024 * 1024);
      if (!typeOk) {
        setError("Only JPG, PNG, or WEBP images are allowed.");
        continue;
      }
      if (sizeMb > MAX_FILE_MB) {
        try {
          const compressed = await compressImage(f);
          next.push(compressed);
        } catch {
          setError(`File too large (> ${MAX_FILE_MB}MB): ${f.name}`);
        }
      } else {
        try {
          const compressed = await compressImage(f);
          next.push(compressed);
        } catch {
          next.push(f);
        }
      }
    }
    setNewFiles(next);
  };

  const removeExistingPhoto = (url) => {
    setExistingPhotoUrls((prev) => prev.filter((u) => u !== url));
    setDeletedPhotoUrls((prev) =>
      prev.includes(url) ? prev : [...prev, url]
    );
  };
  const restorePhoto = (url) => {
    setDeletedPhotoUrls((prev) => prev.filter((u) => u !== url));
    setExistingPhotoUrls((prev) =>
      prev.includes(url) ? prev : [...prev, url]
    );
  };
  const removeNewFile = (name) => {
    setNewFiles((prev) => prev.filter((f) => f.name !== name));
  };
  const moveExistingPhoto = (url, dir) => {
    setExistingPhotoUrls((prev) => {
      const i = prev.indexOf(url);
      if (i < 0) return prev;
      const j =
        dir === "left"
          ? Math.max(0, i - 1)
          : Math.min(prev.length - 1, i + 1);
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };
  const setCoverPhoto = (url) => {
    setExistingPhotoUrls((prev) => {
      const i = prev.indexOf(url);
      if (i <= 0) return prev;
      const next = [...prev];
      next.splice(i, 1);
      next.unshift(url);
      return next;
    });
  };

  const newFilePreviews = useMemo(
    () => newFiles.map((f) => ({ name: f.name, url: URL.createObjectURL(f) })),
    [newFiles]
  );
  useEffect(() => {
    return () => newFilePreviews.forEach((p) => URL.revokeObjectURL(p.url));
  }, [newFilePreviews]);

  /** ------------------------------- Save -------------------------------- **/
  const handleSave = async (opts = { exitAfter: true }) => {
    if (!user?.uid || !id) return;
    setSaving(true);
    setError("");
    setSuccessMsg("");
    setUploadProgress(0);

    try {
      const cleanAreas = normalizeStringList(areas);
      const cleanAmenities = Array.from(
        new Set(
          (amenities || [])
            .map((a) => normalizeAmenityKey(a))
            .filter(Boolean)
        )
      );
      const features = Object.fromEntries(
        cleanAmenities.map((k) => [k, true])
      );

      // Base updates from the form
      const baseUpdates = {
        title: title.trim(),
        summary: summary.trim(),
        capacity: asIntOrNull(capacity) ?? 0,
        address: address.trim(),
        city: city.trim(),
        stateCode: stateCode.trim().toUpperCase(),

        spaceType,
        spaceName: (spaceName || "").trim() || null,
        entranceType,

        areas: cleanAreas,
        amenities: cleanAmenities,
        features,

        bookingMode,
        dayPrice:
          bookingMode !== "overnight"
            ? asIntOrNull(dayPrice) ?? 0
            : null,
        dayUseStartHour:
          bookingMode !== "overnight"
            ? asIntOrNull(dayUseStartHour)
            : null,
        dayUseEndHour:
          bookingMode !== "overnight"
            ? asIntOrNull(dayUseEndHour)
            : null,

        nightlyPrice:
  bookingMode !== "day"
    ? asIntOrNull(nightlyPrice) ?? 0
    : null,

// ✅ legacy fields (keep for other screens)
minNights:
  bookingMode !== "day" ? asIntOrNull(minNights) : null,
maxNights:
  bookingMode !== "day" ? asIntOrNull(maxNights) : null,

// ✅ NEW: canonical rules for HostCalendar + future consistency
bookingRules: {
  minNights:
    bookingMode !== "day" ? asIntOrNull(minNights) : null,
  maxNights:
    bookingMode !== "day" ? asIntOrNull(maxNights) : null,
},

checkInHour:
  bookingMode !== "day" ? asIntOrNull(checkInHour) : null,
checkOutHour:
  bookingMode !== "day" ? asIntOrNull(checkOutHour) : null,


        rules: (rules || "").trim(),

        // Photos delta & order (first is cover)
        existingPhotoUrls,
        deletedPhotoUrls,
        newFiles,
        onProgress: (p) => setUploadProgress(Math.round(p)),
      };

      // If the listing was previously published (or rejected) and the host edits it,
      // kick it back to "pending" and make sure it shows up in the admin queue again.
      const shouldResubmitForReview =
        status === "published" || status === "rejected";

      const updates = {
        ...baseUpdates,
        ...(shouldResubmitForReview && {
          status: "pending",
          submittedAt: serverTimestamp(),
          reviewedAt: null,
          reviewedBy: null,
          rejectionReason: "",
        }),
      };

      await updateListing(id, updates);

      if (shouldResubmitForReview) {
        setStatus("pending");
        setSuccessMsg(
          "Your changes were saved and sent to the team for approval."
        );
      } else {
        setSuccessMsg("Listing updated successfully!");
      }

      setDeletedPhotoUrls([]);
      setNewFiles([]);
      setUploadProgress(100);

      // Refresh dirty snapshot
      initialSnapshotRef.current = JSON.stringify({
        ...JSON.parse(initialSnapshotRef.current || "{}"),
        title: updates.title,
        summary: updates.summary,
        capacity: String(updates.capacity ?? ""),
        address: updates.address,
        city: updates.city,
        stateCode: updates.stateCode,
        spaceType: updates.spaceType,
        spaceName: updates.spaceName || "",
        entranceType: updates.entranceType,
        areas: updates.areas,
        amenities: updates.amenities,
        bookingMode: updates.bookingMode,
        dayPrice: String(updates.dayPrice ?? ""),
        dayUseStartHour: updates.dayUseStartHour ?? "",
        dayUseEndHour: updates.dayUseEndHour ?? "",
        nightlyPrice: String(updates.nightlyPrice ?? ""),
        minNights: String(updates.minNights ?? ""),
        maxNights: String(updates.maxNights ?? ""),
        checkInHour: updates.checkInHour ?? "",
        checkOutHour: updates.checkOutHour ?? "",
        rules: updates.rules,
        existingPhotoUrls,
        deletedPhotoUrls: [],
        newFiles: [],
      });

      if (opts.exitAfter) {
        setTimeout(
          () => navigate("/host/listings", { replace: true }),
          900
        );
      }
    } catch (err) {
      console.error("Update failed:", err);
      setError(
        err?.message || "Could not update listing. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (
      isDirty &&
      !window.confirm("Discard changes and return to listings?")
    )
      return;
    navigate("/host/listings", { replace: true });
  };

  const canSuggestRate = useMemo(
    () =>
      !!city &&
      !!stateCode &&
      Number(capacity) > 0 &&
      bookingMode !== "overnight",
    [city, stateCode, capacity, bookingMode]
  );
  const onSuggestRate = () => {
    try {
      const suggested = suggestDayRate({
        city,
        stateCode,
        capacity: Number(capacity || 0),
      });
      setDayPrice(String(suggested));
    } catch {
      // ignore
    }
  };

  // New: detect if location changed from original
  const hasLocationChanged =
    (address || "").trim() !== (originalLocationRef.current.address || "") ||
    (city || "").trim() !== (originalLocationRef.current.city || "") ||
    stateCode.trim().toUpperCase() !==
      (originalLocationRef.current.stateCode || "");

  /** ------------------------------- Render ------------------------------ **/
  if (loading || loadingListing) {
    return (
      <div className="min-h-screen bg-white text-neutral-900">
        <main className="mx-auto max-w-3xl px-4 py-16">
          <div className="animate-pulse rounded-2xl border border-neutral-200 p-6">
            <div className="h-4 w-44 rounded bg-neutral-200" />
            <div className="mt-4 h-3 w-full rounded bg-neutral-200" />
          </div>
        </main>
      </div>
    );
  }

  if (error && !title) {
    return (
      <div className="min-h-screen bg-white text-neutral-900">
        <main className="mx-auto max-w-3xl px-4 py-16">
          <div className="rounded-2xl border border-red-200 bg-red-50 p-6">
            <div className="text-red-700">{error}</div>
            <Link
              to="/host/listings"
              className="mt-4 inline-block rounded-full border border-neutral-300 px-4 py-2 text-sm hover:border-neutral-400"
            >
              Back to listings
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-neutral-900">
      {/* Header */}
      <header className="sticky top-0 z-30 w-full border-b border-neutral-200 bg-white/70 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <Link to="/" className="flex items-center gap-2">
            <div
              className="h-6 w-6 rounded-lg"
              style={{ backgroundColor: BRAND }}
            />
            <span className="text-sm font-semibold">{BRAND_NAME}</span>
          </Link>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleSave({ exitAfter: false })}
              disabled={saving}
              className="rounded-full border border-neutral-300 px-5 py-2 text-sm font-semibold hover:bg-neutral-100 disabled:opacity-60"
            >
              Save & stay
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="rounded-full px-5 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
              style={{ backgroundColor: BRAND }}
            >
              {saving ? "Saving..." : "Save & exit"}
            </button>
            <button
              onClick={handleCancel}
              className="rounded-full px-4 py-2 text-sm hover:bg-neutral-100"
            >
              Cancel
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-8">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">Edit listing</h1>
          {status && (
            <span
              className={clsx(
                "rounded-full px-3 py-1 text-xs font-semibold",
                status === "published" &&
                  "bg-emerald-50 text-emerald-700 border border-emerald-200",
                status === "pending" &&
                  "bg-amber-50 text-amber-700 border border-amber-200",
                status === "draft" &&
                  "bg-neutral-50 text-neutral-700 border border-neutral-200",
                status === "rejected" &&
                  "bg-red-50 text-red-700 border border-red-200"
              )}
            >
              {status}
            </span>
          )}
        </div>

        {/* Heads up for live listings: edits trigger re-review */}
        {status === "published" && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            Any changes you save will be sent back to the team for approval
            before they go live to guests.
          </div>
        )}
        {status === "rejected" && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            If you update this listing and save, it will be resubmitted to the
            team for another review.
          </div>
        )}

        {!!verification &&
          (verification?.idVerification?.status !== "verified" ||
            verification?.rightToList?.status !== "verified") && (
            <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              Heads up: your verification is still in progress. Edits save
              normally, but publishing or promoting may be delayed until
              verification completes.
            </div>
          )}

        {successMsg && (
          <div className="mb-4 rounded-xl border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
            {successMsg}
          </div>
        )}
        {error && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
        {uploadProgress > 0 && uploadProgress < 100 && (
          <div className="mb-4">
            <div className="flex items-center justify-between text-xs text-neutral-500">
              <span>Uploading…</span>
              <span>{uploadProgress}%</span>
            </div>
            <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-neutral-200">
              <div
                className="h-full transition-all"
                style={{ width: `${uploadProgress}%`, backgroundColor: BRAND }}
              />
            </div>
          </div>
        )}

        <div className="rounded-2xl border border-neutral-200 bg-white p-6">
          {/* Basic Information */}
          <section className="mb-8">
            <h2 className="mb-4 text-lg font-semibold">Basic information</h2>
            <div className="grid gap-4">
              <div>
                <label className="text-sm">Title</label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
                  placeholder="Sunny modern home with large yard"
                />
              </div>
              <div>
                <label className="text-sm">Summary</label>
                <textarea
                  rows={4}
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
                  placeholder="Great for small gatherings, shoots, or overnight stays…"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm">Max headcount</label>
                  <input
                    type="number"
                    min={1}
                    value={capacity}
                    onChange={(e) => setCapacity(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Location */}
          <section className="mb-8">
            <h2 className="mb-4 text-lg font-semibold">Location</h2>

            {hasLocationChanged && (
              <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                You updated the address for this listing. After you save,{" "}
                <span className="font-semibold">{BRAND_NAME}</span> may run a quick
                verification on the new location. Your changes will still be saved,
                and you can continue editing normally.
              </div>
            )}

            <div className="grid gap-4">
              <div>
                <label className="text-sm">Street address</label>
                <input
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
                  placeholder="123 Main St"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2">
                  <label className="text-sm">City</label>
                  <input
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
                    placeholder="St. Louis"
                  />
                </div>
                <div>
                  <label className="text-sm">State</label>
                  <input
                    value={stateCode}
                    onChange={(e) =>
                      setStateCode(
                        e.target.value.toUpperCase().slice(0, 2)
                      )
                    }
                    maxLength={2}
                    className="mt-1 w-full rounded-xl border border-neutral-300 px-3 py-2 text-sm uppercase outline-none focus:border-neutral-400"
                    placeholder="MO"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Space details */}
          <section className="mb-8">
            <h2 className="mb-4 text-lg font-semibold">Space details</h2>
            <div className="grid gap-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm">Space type</label>
                  <select
                    value={spaceType}
                    onChange={(e) => setSpaceType(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
                  >
                    <option value="entire_place">Entire place</option>
                    <option value="private_room">Private room</option>
                    <option value="shared_space">Shared space</option>
                    <option value="outdoor">Outdoor area</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm">Entrance</label>
                  <select
                    value={entranceType}
                    onChange={(e) => setEntranceType(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
                  >
                    <option value="private">Private</option>
                    <option value="shared">Shared</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm">Space name (optional)</label>
                  <input
                    value={spaceName}
                    onChange={(e) => setSpaceName(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
                    placeholder="Coach house, studio, pool, yard…"
                  />
                </div>
              </div>

              {/* Areas (free-form chips) */}
              <div>
                <label className="text-sm">Areas (comma or Enter)</label>
                <TagInput
                  values={areas}
                  onChange={setAreas}
                  placeholder="living room, backyard, kitchen…"
                />
              </div>

              {/* Amenities */}
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="text-sm font-medium">Amenities</label>
                  <AmenityAddInline
                    onAdd={(raw) =>
                      setAmenities((prev) => {
                        const k = normalizeAmenityKey(raw);
                        if (!k) return prev;
                        const set = new Set(prev);
                        set.add(k);
                        return Array.from(set);
                      })
                    }
                  />
                </div>
                <AmenityGridClean
                  selected={amenities}
                  onChange={setAmenities}
                />
                <div className="mt-2 text-xs text-neutral-500">
                  Selected: {amenities.length}
                </div>
              </div>
            </div>
          </section>

          {/* Photos */}
          <section className="mb-8">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Photos</h2>
              <div className="text-xs text-neutral-500">
                {existingPhotoUrls.length + newFiles.length}/{MAX_FILES}
              </div>
            </div>

            {existingPhotoUrls.length > 0 && (
              <>
                <div className="mb-2 text-sm text-neutral-600">
                  Current photos (first is cover)
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {existingPhotoUrls.map((url, i) => (
                    <div
                      key={url}
                      className="group relative overflow-hidden rounded-xl border border-neutral-200"
                    >
                      <img
                        src={url}
                        alt={`Photo ${i + 1}`}
                        className="h-32 w-full object-cover"
                      />
                      <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-black/30 px-2 py-1 text-[11px] text-white">
                        <span className="truncate">
                          {i === 0 ? "Cover" : `Photo ${i + 1}`}
                        </span>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() =>
                              moveExistingPhoto(url, "left")
                            }
                            className="rounded bg-white/90 px-1 text-[10px] text-neutral-800 hover:bg-white"
                            title="Move left"
                          >
                            ←
                          </button>
                          <button
                            onClick={() =>
                              moveExistingPhoto(url, "right")
                            }
                            className="rounded bg-white/90 px-1 text-[10px] text-neutral-800 hover:bg-white"
                            title="Move right"
                          >
                            →
                          </button>
                          <button
                            onClick={() => setCoverPhoto(url)}
                            className="rounded bg-white/90 px-1 text-[10px] text-neutral-800 hover:bg-white"
                            title="Make cover"
                          >
                            ★
                          </button>
                        </div>
                      </div>
                      <button
                        onClick={() => removeExistingPhoto(url)}
                        className="absolute right-2 top-2 rounded-full bg-white/90 px-2 py-1 text-xs shadow hover:bg-white"
                        title="Remove"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}

            {deletedPhotoUrls.length > 0 && (
              <div className="mt-4">
                <div className="mb-2 text-sm text-neutral-600">
                  Marked for deletion
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {deletedPhotoUrls.map((url) => (
                    <div
                      key={url}
                      className="relative overflow-hidden rounded-xl border border-red-200 opacity-60"
                    >
                      <img
                        src={url}
                        alt="Deleted"
                        className="h-32 w-full object-cover"
                      />
                      <button
                        onClick={() => restorePhoto(url)}
                        className="absolute right-2 top-2 rounded-full bg-white/90 px-2 py-1 text-xs shadow hover:bg-white"
                      >
                        Restore
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {newFiles.length > 0 && (
              <div className="mt-4">
                <div className="mb-2 text-sm text-neutral-600">
                  New photos (to upload)
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {newFilePreviews.map((p) => (
                    <div
                      key={p.name}
                      className="group relative overflow-hidden rounded-xl border border-neutral-200"
                    >
                      <img
                        src={p.url}
                        alt={p.name}
                        className="h-32 w-full object-cover"
                      />
                      <div className="absolute inset-x-0 bottom-0 bg-white/80 p-1 text-[11px] truncate">
                        {p.name}
                      </div>
                      <button
                        onClick={() => removeNewFile(p.name)}
                        className="absolute right-2 top-2 rounded-full bg-white/90 px-2 py-1 text-xs shadow hover:bg-white"
                        title="Remove"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Picker & DnD */}
            <div
              className="mt-4 rounded-xl border border-dashed border-neutral-300 p-4 text-center text-sm hover:bg-neutral-50"
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onDrop={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                await onPickFiles(e.dataTransfer.files);
              }}
            >
              <div>Drag & drop images here, or</div>
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="rounded-full border border-neutral-300 px-3 py-1.5 text-sm hover:bg-neutral-100"
                >
                  Choose files
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPTED_TYPES.join(",")}
                  multiple
                  hidden
                  onChange={async (e) => {
                    await onPickFiles(e.target.files);
                    e.currentTarget.value = "";
                  }}
                />
              </div>
              <div className="mt-2 text-xs text-neutral-500">
                Up to {MAX_FILES} total. We’ll lightly compress to speed up
                uploads.
              </div>
            </div>
          </section>

          {/* Pricing & Booking mode */}
          <section className="mb-8">
            <h2 className="mb-4 text-lg font-semibold">Pricing</h2>
            <div className="mb-4">
              <label className="text-sm">Booking mode</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {[
                  { k: "day", label: "Daytime only" },
                  { k: "overnight", label: "Overnight" },
                  { k: "hybrid", label: "Day + Overnight" },
                ].map((opt) => (
                  <button
                    key={opt.k}
                    type="button"
                    onClick={() => setBookingMode(opt.k)}
                    className={clsx(
                      "rounded-full border px-3 py-1.5 text-sm",
                      bookingMode === opt.k
                        ? "border-transparent text-white"
                        : "border-neutral-300"
                    )}
                    style={
                      bookingMode === opt.k
                        ? { backgroundColor: BRAND }
                        : {}
                    }
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {(bookingMode === "day" || bookingMode === "hybrid") && (
              <div className="mb-4 rounded-xl border border-neutral-200 p-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium">Day pricing</div>
                  <button
                    type="button"
                    onClick={onSuggestRate}
                    disabled={!canSuggestRate}
                    className={clsx(
                      "rounded-full border px-3 py-1.5 text-xs",
                      canSuggestRate ? "hover:bg-neutral-100" : "opacity-50"
                    )}
                  >
                    Suggest rate
                  </button>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm">Day rate (USD)</label>
                    <div className="mt-1 flex items-center gap-2">
                      <span className="rounded-xl border border-neutral-300 px-3 py-2 text-sm">
                        $
                      </span>
                      <input
                        type="number"
                        min={1}
                        value={dayPrice}
                        onChange={(e) => setDayPrice(e.target.value)}
                        className="w-40 rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm">
                      Day use start (hour 0–23)
                    </label>
                    <input
                      type="number"
                      min={0}
                      max={23}
                      value={dayUseStartHour}
                      onChange={(e) => setDayUseStartHour(e.target.value)}
                      className="mt-1 w-full rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
                      placeholder="9"
                    />
                  </div>
                  <div>
                    <label className="text-sm">
                      Day use end (hour 0–23)
                    </label>
                    <input
                      type="number"
                      min={0}
                      max={23}
                      value={dayUseEndHour}
                      onChange={(e) => setDayUseEndHour(e.target.value)}
                      className="mt-1 w-full rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
                      placeholder="17"
                    />
                  </div>
                </div>
              </div>
            )}

            {(bookingMode === "overnight" || bookingMode === "hybrid") && (
              <div className="rounded-xl border border-neutral-200 p-4">
                <div className="text-sm font-medium">
                  Overnight pricing
                </div>
                <div className="mt-3 grid gap-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm">Nightly rate (USD)</label>
                      <div className="mt-1 flex items-center gap-2">
                        <span className="rounded-xl border border-neutral-300 px-3 py-2 text-sm">
                          $
                        </span>
                        <input
                          type="number"
                          min={1}
                          value={nightlyPrice}
                          onChange={(e) =>
                            setNightlyPrice(e.target.value)
                          }
                          className="w-40 rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm">Min nights</label>
                      <input
                        type="number"
                        min={1}
                        value={minNights}
                        onChange={(e) =>
                          setMinNights(e.target.value)
                        }
                        className="mt-1 w-full rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
                      />
                    </div>
                    <div>
                      <label className="text-sm">
                        Max nights (optional)
                      </label>
                      <input
                        type="number"
                        min={1}
                        value={maxNights}
                        onChange={(e) =>
                          setMaxNights(e.target.value)
                        }
                        className="mt-1 w-full rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm">
                        Check-in hour (0–23)
                      </label>
                      <input
                        type="number"
                        min={0}
                        max={23}
                        value={checkInHour}
                        onChange={(e) =>
                          setCheckInHour(e.target.value)
                        }
                        className="mt-1 w-full rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
                      />
                    </div>
                    <div>
                      <label className="text-sm">
                        Check-out hour (0–23)
                      </label>
                      <input
                        type="number"
                        min={0}
                        max={23}
                        value={checkOutHour}
                        onChange={(e) =>
                          setCheckOutHour(e.target.value)
                        }
                        className="mt-1 w-full rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* Things to know (guest-facing) */}
          <section className="mb-8">
            <h2 className="mb-4 text-lg font-semibold">Things to know</h2>
            <p className="mb-3 text-sm text-neutral-600">
              This is what guests see on your listing under “Things to know”.
            </p>

            <div className="space-y-4 text-sm text-neutral-700">
              <div>
                <div className="font-medium">House rules</div>
                <p className="mt-1 text-neutral-600">
                  Your house rules below are shown to guests before they book and in
                  their confirmation.
                </p>
              </div>

              <div>
                <div className="font-medium">Cancellation policy</div>
                <ul className="mt-1 list-disc space-y-1 pl-5 text-neutral-600">
                  <li>
                    Free cancellation of the stay price within 24 hours of booking,
                    as long as check-in is at least 48 hours away. Service fees and
                    taxes are not refunded.
                  </li>
                  <li>
                    50% refund of the stay price for cancellations more than 7 days
                    before check-in. Service fees and taxes are not refunded.
                  </li>
                  <li>
                    No refund 7 days or less before check-in, or after the stay has
                    started.
                  </li>
                  <li>
                    If the booking is still “Pending host approval”, guests can
                    cancel before you approve and their card is not charged.
                  </li>
                  <li>
                    If you cancel a booking before the stay starts, the guest
                    receives a full refund of their total payment (including fees
                    and taxes).
                  </li>
                </ul>
              </div>
            </div>

            <div className="mt-3 rounded-xl bg-neutral-50 p-3 text-xs text-neutral-500">
              Cancellation rules are set by {BRAND_NAME} and apply to all listings.
              You can adjust house rules below for this specific space.
            </div>
          </section>

          {/* House rules */}
          <section>
            <h2 className="mb-4 text-lg font-semibold">House rules</h2>
            <textarea
              rows={4}
              value={rules}
              onChange={(e) => setRules(e.target.value)}
              className="w-full rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
              placeholder="Quiet hours, parking instructions, no parties, etc."
            />
          </section>
        </div>
      </main>
    </div>
  );
}

/** ------------------------------ TagInput ------------------------------ **/
function TagInput({ values, onChange, placeholder }) {
  const [text, setText] = useState("");
  const add = useCallback(
    (v) => {
      const clean = String(v || "").trim();
      if (!clean) return;
      const next = Array.from(new Set([...(values || []), clean]));
      onChange(next);
    },
    [values, onChange]
  );
  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      add(text);
      setText("");
    } else if (e.key === "Backspace" && !text && values?.length) {
      onChange(values.slice(0, -1));
    }
  };
  return (
    <div className="rounded-xl border border-neutral-300 p-2">
      <div className="flex flex-wrap gap-2">
        {(values || []).map((v) => (
          <span
            key={v}
            className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-2 py-1 text-xs"
          >
            {v}
            <button
              type="button"
              onClick={() => onChange(values.filter((x) => x !== v))}
              className="rounded-full px-1 text-neutral-500 hover:bg-neutral-200"
              aria-label={`Remove ${v}`}
            >
              ×
            </button>
          </span>
        ))}
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          className="min-w-[12ch] flex-1 px-2 text-sm outline-none"
        />
      </div>
      <div className="mt-1 px-1 text-[11px] text-neutral-500">
        Press Enter or comma to add
      </div>
    </div>
  );
}

/** ---------------------------- Amenities UI --------------------------- **/
// Minimal inline adder. Whatever you add becomes a normal tile you can toggle on/off.
function AmenityAddInline({ onAdd }) {
  const [text, setText] = useState("");
  const commit = () => {
    const t = text.trim();
    if (!t) return;
    onAdd(t);
    setText("");
  };
  return (
    <div className="flex items-center gap-2">
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && commit()}
        placeholder="Add amenity"
        className="w-40 rounded-xl border border-neutral-300 px-2 py-1.5 text-xs outline-none focus:border-neutral-400"
      />
      <button
        type="button"
        onClick={commit}
        className="rounded-full border border-neutral-300 px-2.5 py-1.5 text-xs hover:bg-neutral-100"
      >
        Add
      </button>
    </div>
  );
}

// Single, neutral grid of toggle tiles.
function AmenityGridClean({ selected, onChange }) {
  const [query, setQuery] = useState("");

  const baseOrdered = AMENITY_ORDER.slice();
  const selectedSet = useMemo(
    () => new Set((selected || []).map((k) => normalizeAmenityKey(k))),
    [selected]
  );

  // Add any selected keys not in AMENITY_ORDER to the end (“customs/extras”)
  const extras = useMemo(
    () =>
      Array.from(selectedSet).filter((k) => !baseOrdered.includes(k)),
    [selectedSet]
  );
  const allKeys = useMemo(
    () => [...baseOrdered, ...extras],
    [baseOrdered, extras]
  );

  const items = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allKeys
      .map((key) => {
        const k = normalizeAmenityKey(key);
        const label = AMENITY_CATALOG[k]?.label || titleCaseFromKey(k);
        return { key: k, label };
      })
      .filter((it) =>
        q ? it.label.toLowerCase().includes(q) || it.key.includes(q) : true
      );
  }, [allKeys, query]);

  const toggle = (kRaw) => {
    const k = normalizeAmenityKey(kRaw);
    const out = new Set(selectedSet);
    if (out.has(k)) out.delete(k);
    else out.add(k);
    onChange(Array.from(out));
  };

  const clearAll = () => onChange([]);

  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
          placeholder="Search amenities…"
        />
        {selected?.length ? (
          <button
            type="button"
            onClick={clearAll}
            className="rounded-full border border-neutral-300 px-3 py-2 text-xs hover:bg-neutral-100"
            title="Clear all"
          >
            Clear
          </button>
        ) : null}
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {items.map((it) => {
          const isOn = selectedSet.has(it.key);
          return (
            <button
              key={it.key}
              type="button"
              onClick={() => toggle(it.key)}
              className={clsx(
                "flex items-center justify-between rounded-xl border px-3 py-2 text-left text-sm transition",
                isOn
                  ? "border-transparent text-white"
                  : "border-neutral-300 hover:bg-neutral-50"
              )}
              style={isOn ? { backgroundColor: BRAND } : {}}
            >
              <span className="truncate">{it.label}</span>
              <span
                className={clsx(
                  "ml-2 inline-flex h-4 w-4 items-center justify-center rounded border text-[10px]",
                  isOn
                    ? "border-white bg-white/20 text-white"
                    : "border-neutral-300 text-transparent"
                )}
                aria-hidden
              >
                ✓
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
